import { unwrap } from "@sagot/api-client";
import { OTP_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ErrorState, Label, QrCode, Skeleton, toast } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { OpenAuthenticatorLink } from "../../../../auth/components";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { CopyTextButton } from "./panel-kit";
import { CodeInput } from "./two-factor-dialogs";

/** Where the setup panel is: waiting for a key, failed to get one, or showing it. */
export type SetupState = { status: "loading" } | { status: "failed"; message: string } | { status: "ready"; secret: string; uri: string };

/** Groups the base32 key in fours for reading aloud / typing ("JBSW Y3DP …"). */
export function groupKey(secret: string): string {
  return secret.replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
}

/** The QR sits beside the key on larger screens and below it on a phone, which cannot scan its own screen. */
const SCAN_AND_KEY_LAYOUT = "mt-3 flex flex-col-reverse gap-6 sm:flex-row sm:items-start sm:gap-11";

function SetupSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="card-body space-y-5 border-t border-line" role="status" aria-busy="true" data-testid="tfa-setup-loading">
      <span className="sr-only">{t("settings.twoFactor.preparing")}</span>
      <div className="space-y-3">
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-4 w-2/5" />
        <div className={SCAN_AND_KEY_LAYOUT}>
          <Skeleton className="mx-auto size-44 sm:mx-0" />
          <div className="flex-1 space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-9 w-28" />
          </div>
        </div>
        <Skeleton className="h-4 w-3/5" />
        <Skeleton className="h-9 w-64" />
      </div>
    </div>
  );
}

function SetupKey({ secret, uri }: { secret: string; uri: string }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 flex-1 space-y-3">
      <div>
        <p className="label">{t("settings.twoFactor.keyLabel")}</p>
        <p className="break-words rounded-md border border-line bg-sunken px-3 py-2 font-mono text-sm text-fg" data-testid="totp-secret">
          {groupKey(secret)}
        </p>
      </div>
      <CopyTextButton value={secret} label={t("settings.twoFactor.copyKey")} className="max-sm:h-11 max-sm:w-full" />
      <p className="hint sm:hidden">{t("settings.twoFactor.sameDeviceHint")}</p>
      <OpenAuthenticatorLink uri={uri} label={t("settings.twoFactor.openApp")} />
    </div>
  );
}

function SetupReady({ secret, uri, onVerified }: { secret: string; uri: string; onVerified: (codes: string[]) => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await unwrap(api.POST("/v1/me/2fa/enable", { body: { code } }));
      onVerified(result.backup_codes);
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card-body space-y-4 border-t border-line" data-testid="tfa-setup">
      <ol className="list-decimal space-y-3 pl-5 text-sm text-fg-muted">
        <li>{t("settings.twoFactor.step1")}</li>
        <li>
          {t("settings.twoFactor.step2")}
          <div className={SCAN_AND_KEY_LAYOUT}>
            <div className="mx-auto shrink-0 rounded-lg border border-line bg-white p-2 sm:mx-0" data-testid="tfa-qr">
              <QrCode value={uri} label={t("settings.twoFactor.qrLabel")} />
            </div>
            <SetupKey secret={secret} uri={uri} />
          </div>
        </li>
        <li>{t("settings.twoFactor.step3")}</li>
      </ol>
      <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void verify(event)}>
        <div>
          <Label htmlFor="tfa-code">{t("settings.twoFactor.codeLabel")}</Label>
          <CodeInput id="tfa-code" value={code} onChange={setCode} />
        </div>
        <Button type="submit" loading={busy} disabled={code.length < OTP_LENGTH}>
          {t("settings.twoFactor.verify")}
        </Button>
      </form>
    </div>
  );
}

interface TwoFactorSetupProps {
  state: SetupState;
  onRetry: () => void;
  onVerified: (codes: string[]) => void;
}

/** The setup step of the two-factor card: skeleton while the key is made, a retry if it fails, then QR + key + code field. */
export function TwoFactorSetup({ state, onRetry, onVerified }: TwoFactorSetupProps) {
  const { t } = useTranslation();
  if (state.status === "loading") return <SetupSkeleton />;
  if (state.status === "failed") {
    return (
      <div className="card-body border-t border-line" data-testid="tfa-setup-error">
        <ErrorState headingLevel={3} title={t("settings.twoFactor.loadErrorTitle")} description={state.message} onRetry={onRetry} retryLabel={t("common.retry")} />
      </div>
    );
  }
  return <SetupReady secret={state.secret} uri={state.uri} onVerified={onVerified} />;
}
